import './CitySelector.scss';

import type { TCities, TCitiesSelectorProps } from '../../utils/types';
import { useData, useHandlers } from './hooks';
import { useEffect, useRef, useState } from 'react';

import SearchInput from '../SearchInput/SearchInput';
import { highlightText } from '../../utils/helpers/highlightText';
import { useTranslation } from '../../utils';

const CitySelector = ({ options, field, form, initialValue }: TCitiesSelectorProps) => {
  const [loadedItems, setLoadedItems] = useState(options.slice(0, 49));
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const { t } = useTranslation();
  const { isOpen, openSelect, closeSelect, handleSearchInputChange, selectedValue, searchValue, onOptionClick } =
    useHandlers({
      form,
      field,
      initialValue,
    });
  const { filteredOptions, handleScroll } = useData({ options, searchValue, loadedItems, setLoadedItems });

  const showCityError = form.submitCount > 0 && !!form.errors.cityCode && !field.value;

  useEffect(() => {
    if (isOpen) {
      const nextPage = loadedItems.length / 50 + 1;
      const newItems = filteredOptions?.slice(0, nextPage * 50);
      setLoadedItems(newItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filteredOptions]);

  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        closeSelect();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, closeSelect]);

  return (
    <div className='citySelectorWrap' ref={wrapperRef}>
      <label htmlFor='city'>
        <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
          <path d='M12 21s-7-6.5-7-11a7 7 0 0 1 14 0c0 4.5-7 11-7 11z' strokeLinecap='round' strokeLinejoin='round' />
          <circle cx='12' cy='10' r='2.5' />
        </svg>
        {t('Sign up (client).Select_city')}
      </label>
      <input
        className={`form-control${showCityError ? ' error' : ''}`}
        name='city'
        id='city'
        onClick={openSelect}
        value={selectedValue}
        placeholder={showCityError ? String(form.errors.cityCode) : t('Web.Placeholder_city')}
        readOnly
      />

      {isOpen && (
        <div className='cityDropdown' onScroll={handleScroll}>
          <div className='searchWrapper'>
            <SearchInput handleChangeSearchValue={handleSearchInputChange} />
          </div>
          <div className='listContainer'>
            {loadedItems.map((option: TCities, index: number) => (
              <div className='listItem' key={index} onClick={onOptionClick(option.name, option.cityId)}>
                <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                  <path d='M12 21s-7-6.5-7-11a7 7 0 0 1 14 0c0 4.5-7 11-7 11z' strokeLinecap='round' strokeLinejoin='round' />
                  <circle cx='12' cy='10' r='2.5' />
                </svg>
                <span>{highlightText(option.name, searchValue, 'highlight')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CitySelector;